import { useModal } from "@/Contexts";
import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { useTerminalsState } from "@/States/Terminals";
import { getRandomInteger } from "@/Utils/getRandomInteger";
import { trpc } from "@/Utils/trpc";
import { useConfig } from "@/api/hooks/useConfig";
import {
  DevelopmentInWasteTypesMWM,
  DevelopmentInWasteTypesRE,
} from "@/components/Graphs/DevelopmentInWasteTypes/index";
import {
  InfrastructureMWM,
  InfrastructureRE,
} from "@/components/Graphs/InfrastructureSummary/index";
import {
  OverviewKeyNumbersMWM,
  OverviewKeyNumbersRE,
} from "@/components/Graphs/OverviewKeyNumbers/index";
import { OverviewKeyNumbersCategory } from "@/components/Graphs/OverviewKeyNumbersCategory";
import { PieWasteContribution } from "@/components/Graphs/PieWasteContribution";
import {
  RecyclingRateOverTimeMWM,
  RecyclingRateOverTimeRE,
} from "@/components/Graphs/RecyclingRateOverTime/index";
import {
  WasteTypeDistributionMWM,
  WasteTypeDistributionRE,
} from "@/components/Graphs/WasteStreamDistribution/index";
import { WasteTypesInUseCard } from "@/components/Graphs/WasteTypesInUse";
import { Page } from "@/components/Page";
import NoTerminals from "@/pages/error/NoTerminals";
import { useTrans } from "@/translations";

import { useCallback, useMemo, useState } from "react";

//@ts-ignore
import html2pdf from "html2pdf.js";
import moment from "moment";
import type { FilterTimeFrameValue } from "server/config-api/index.js";
import DashboardTileType from "server/utils/dashboardTileType.js";

import { useHomeLoadingState } from "./loadingState";
import { PdfPreview, usePdfPreview } from "./pdfPreview";

const NR_UNDERTEXT_VARIANTS = 3;

type PersistedGraphTimeFilters = {
  [k in DashboardTileType]?: FilterTimeFrameValue;
};

export const Home = () => {
  const { t } = useTrans();
  const { currentTerminal } = useTerminalsState();
  const { isRealEstate } = useConfig();
  const { setPdfPreviewOpen } = usePdfPreview();
  const { data: tileConfig, isLoading } =
    trpc.config.getDashboardTiles.useQuery();
  const { showModal, hideModal } = useModal();
  const [timeFilters, setTimeFilters] = useState<PersistedGraphTimeFilters>({});

  const {
    keyNumbersLoading,
    wasteTypesInUseLoading,
    recyclingRateLoading,
    developmentLoading,
    wasteContributionLoading,
    wasteDistributionLoading,
    comparisonWasteLoading,
    comparisonDistributionLoading,
  } = useHomeLoadingState();

  const isActive = useCallback(
    (tileType: DashboardTileType) =>
      tileConfig?.find((tile) => tile.tileType === tileType)?.visible ?? true,
    [tileConfig],
  );

  const _setTimeFilters = useCallback(
    (value: FilterTimeFrameValue, type: DashboardTileType) => {
      setTimeFilters((prev: PersistedGraphTimeFilters) => ({
        ...prev,
        [type]: value,
      }));
    },
    [setTimeFilters],
  );

  const realEstateView = useMemo(
    () => (
      <>
        {isActive(DashboardTileType.KeyNumbers) && (
          <OverviewKeyNumbersRE
            timeFilter={timeFilters[DashboardTileType.KeyNumbers]}
            setTimeFilters={_setTimeFilters}
          />
        )}
        {isActive(DashboardTileType.KeyNumbersPerCategory) && (
          <OverviewKeyNumbersCategory
            timeFilter={timeFilters[DashboardTileType.KeyNumbersPerCategory]}
            setTimeFilters={_setTimeFilters}
          />
        )}
        {isActive(DashboardTileType.RecyclingRateOverTime) && (
          <RecyclingRateOverTimeRE
            timeFilter={timeFilters[DashboardTileType.RecyclingRateOverTime]}
            setTimeFilters={_setTimeFilters}
          />
        )}
        {isActive(DashboardTileType.DevelopmentInWasteTypes) && (
          <DevelopmentInWasteTypesRE
            timeFilter={timeFilters[DashboardTileType.DevelopmentInWasteTypes]}
            setTimeFilters={_setTimeFilters}
          />
        )}
        {isActive(DashboardTileType.WasteContribution) && (
          <PieWasteContribution
            timeFilter={timeFilters[DashboardTileType.WasteContribution]}
            setTimeFilters={_setTimeFilters}
          />
        )}
        {isActive(DashboardTileType.WasteTypeDistribution) && (
          <WasteTypeDistributionRE
            timeFilter={timeFilters[DashboardTileType.WasteTypeDistribution]}
            setTimeFilters={_setTimeFilters}
          />
        )}
        {isActive(DashboardTileType.WasteTypesInUse) && (
          <WasteTypesInUseCard
            timeFilter={timeFilters[DashboardTileType.WasteTypesInUse]}
            setTimeFilters={_setTimeFilters}
          />
        )}
        {isActive(DashboardTileType.InfrastructureSummary) && (
          <InfrastructureRE />
        )}
      </>
    ),
    [isActive, timeFilters, _setTimeFilters],
  );

  const mwmView = useMemo(
    () => (
      <>
        {isActive(DashboardTileType.KeyNumbers) && (
          <OverviewKeyNumbersMWM
            timeFilter={timeFilters[DashboardTileType.KeyNumbers]}
            setTimeFilters={_setTimeFilters}
          />
        )}
        {isActive(DashboardTileType.DevelopmentInWasteTypes) && (
          <DevelopmentInWasteTypesMWM
            timeFilter={timeFilters[DashboardTileType.DevelopmentInWasteTypes]}
            setTimeFilters={_setTimeFilters}
          />
        )}
        {isActive(DashboardTileType.RecyclingRateOverTime) && (
          <RecyclingRateOverTimeMWM
            timeFilter={timeFilters[DashboardTileType.RecyclingRateOverTime]}
            setTimeFilters={_setTimeFilters}
          />
        )}
        {isActive(DashboardTileType.WasteTypeDistribution) && (
          <WasteTypeDistributionMWM
            timeFilter={timeFilters[DashboardTileType.WasteTypeDistribution]}
            setTimeFilters={_setTimeFilters}
          />
        )}
        {isActive(DashboardTileType.InfrastructureSummary) && (
          <InfrastructureMWM />
        )}
      </>
    ),
    [isActive, timeFilters, _setTimeFilters],
  );

  const view = useMemo(
    () => (isRealEstate ? realEstateView : mwmView),
    [isRealEstate, realEstateView, mwmView],
  );

  const generatePdf = useCallback(async () => {
    const el = document.getElementById("page-content");

    if (el) {
      const margin = 10;
      const PDF_WIDTH = el.getBoundingClientRect().width;
      const PDF_HEIGHT = el.getBoundingClientRect().height;
      const opt = {
        filename: `${t("exportLabels:homeFileName")}_${moment().format("YYYYMMDD")}.pdf`,
        image: { type: "jpeg" as const, quality: 0.98 },
        margin: 10,
        html2canvas: {
          width: PDF_WIDTH,
          height: PDF_HEIGHT,
          windowWidth: window.innerWidth,
          windowHeight: PDF_HEIGHT,
          letterRendering: true,
          useCORS: true,
          backgroundColor: "#F7F2F2",
        },
        jsPDF: {
          unit: "px",
          format: [PDF_WIDTH, PDF_HEIGHT + margin * 2] as [number, number],
          orientation:
            PDF_WIDTH > PDF_HEIGHT
              ? ("landscape" as const)
              : ("portrait" as const),
          hotfixes: ["px_scaling"],
        },
      };
      await html2pdf().set(opt).from(el).save();
    }
  }, [t]);

  const homepageGreeting = useMemo(() => {
    let title = "";
    let undertext = "";

    const variant = getRandomInteger(1, NR_UNDERTEXT_VARIANTS);

    const hour = moment().hour();
    if (hour >= 5 && hour < 12) {
      title = "genericLabels:goodMorning";
      undertext = `genericLabels:goodMorningUndertext${variant}`;
    }
    if (hour >= 12 && hour < 17) {
      title = "genericLabels:goodAfternoon";
      undertext = `genericLabels:goodAfternoonUndertext${variant}`;
    }
    if (hour >= 17 && hour < 22) {
      title = "genericLabels:goodEvening";
      undertext = `genericLabels:goodEveningUndertext${variant}`;
    }
    if (hour >= 22 || hour < 5) {
      title = "genericLabels:stillUp";
      undertext = `genericLabels:stillUpUndertext${variant}`;
    }

    return {
      title,
      undertext,
    };
  }, []);

  const dataLoading = useMemo(
    () =>
      keyNumbersLoading ||
      wasteTypesInUseLoading ||
      recyclingRateLoading ||
      developmentLoading ||
      wasteContributionLoading ||
      wasteDistributionLoading ||
      comparisonWasteLoading ||
      comparisonDistributionLoading,
    [
      keyNumbersLoading,
      wasteTypesInUseLoading,
      recyclingRateLoading,
      developmentLoading,
      wasteContributionLoading,
      wasteDistributionLoading,
      comparisonWasteLoading,
      comparisonDistributionLoading,
    ],
  );

  return (
    <Page
      title={homepageGreeting.title}
      undertext={homepageGreeting.undertext}
      button={{
        label: "actions:previewPDF",
        onClick: () => {
          setPdfPreviewOpen(true);
          showModal(
            <PdfPreview
              generatePdf={generatePdf}
              close={() => {
                setPdfPreviewOpen(false);
                hideModal();
              }}
              view={view}
            />,
          );
        },
        type: "button",
        color: "primary",
        loading: dataLoading,
        className: "disabled:cursor-not-allowed disabled:bg-lime",
      }}
    >
      {isRealEstate && !currentTerminal?.id ? (
        <NoTerminals />
      ) : isLoading ? (
        <div className="flex justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">{view}</div>
      )}
    </Page>
  );
};
